import React, { useMemo, useRef } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { get } from "lodash";

import YesNoRadioButton from "Components/RegisterBuildingForm/YesNoRadioButton";
import InputFieldText from "Components/Common/InputFieldText";
import FireFightingPumpDetails from "./FireFightingPumpDetails";
import FloorWiseFireExtinguisherDetails from "./FloorWiseFireExtinguisherDetails";
import AutomaticSprinklerSystem from "./AutomaticSprinklerSystem";
import FireDetectionSystem from "./FireDetectionSystem";

import { getImageUrl, onlyNumberKeys } from "utils";
import getFileExtension from "utils/getFileExtension";

import pdfImg from "assets/images/pdf.png";
import { UploadImage } from "./style";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const FireProtection = ({ formik, t, floorOptions }) => {
  const {
    setFieldValue,
    errors,
    touched,
    values: {
      fireProtection: {
        isFirstAidHoseReelProvided,
        isWetControllerSystemProvided,
        isInternalHydrantProvided,
        isYardHydrantProvided,
        causeAndEffectMatrixDocumentUrl,
        causeAndEffectMatrixDocument,
      },
    },
  } = formik;

  const theme = useTheme();
  const ref = useRef();

  const errorMessage = useMemo(
    () => get(errors, "fireProtection.causeAndEffectMatrixDocument", ""),
    [get(errors, "fireProtection.causeAndEffectMatrixDocument", "")]
  );

  const isError = useMemo(
    () =>
      !!get(touched, "fireProtection.causeAndEffectMatrixDocument", "") &&
      !!errorMessage,
    [
      get(touched, "fireProtection.causeAndEffectMatrixDocument", ""),
      errorMessage,
    ]
  );

  const fileChangeHandler = (file) => {
    const fileExtension = getFileExtension(file.name)?.toLowerCase();
    if (!(validFileFormats.includes(fileExtension) && file.size < 10485760)) {
      toast.error(
        "Please upload image or pdf file and size should be less than 10 mb"
      );
    } else {
      setFieldValue("fireProtection.causeAndEffectMatrixDocument", file);
    }
    ref.current.value = "";
  };

  return (
    <Grid container columnSpacing={1} rowSpacing={1}>
      <Grid item xs={12}>
        <Typography className="mb-3 fw-bold" lineHeight={1} variant="h6">
          {t("Fire Protection")}
        </Typography>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t("Type of fire water storage tank provided")}
          </Typography>
        </Grid>
        <Grid item xs={12} className="d-flex gap-2 flex-wrap">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Water Storage Tank Type") + "*"}
              value="UnderGround Tank"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("UnderGround Tank Capacity") + "*"}
              name="fireProtection.underGroundTankCapacity"
              formik={formik}
              type="number"
              placeholder="Enter underground tank capacity"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className="d-flex gap-2 flex-wrap">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Water Storage Tank type") + "*"}
              value="OverHead Tank"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("OverHead Tank Capacity") + "*"}
              name="fireProtection.overHeadTankCapacity"
              formik={formik}
              type="number"
              placeholder="Enter overhead tank capacity"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t("Location of Fire Pump House")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Location") + "*"}
            name="fireProtection.firePumpLocation"
            formik={formik}
            placeholder="Enter Location"
          />
        </Grid>
      </Grid>

      <Grid
        container
        rowSpacing={1}
        columnSpacing={2}
        sx={{ pl: 1, minHeight: "70px" }}
      >
        <YesNoRadioButton
          label="Has first aid hose reel been provided?"
          keyName="fireProtection.isFirstAidHoseReelProvided"
          formik={formik}
          isRequired
          onChange={(e) => {
            setFieldValue("fireProtection.numberOfFirstAidHoseReel", "");
            setFieldValue(
              "fireProtection.isFirstAidHoseReelProvided",
              !!+e.target.value
            );
          }}
        />

        {isFirstAidHoseReelProvided && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Number") + "*"}
              name="fireProtection.numberOfFirstAidHoseReel"
              formik={formik}
              placeholder="Enter Number"
              type="number"
              disabled={!isFirstAidHoseReelProvided}
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={2}
        sx={{ pl: 1, minHeight: "70px" }}
      >
        <YesNoRadioButton
          label="Has the wet riser/ downcomer system, been provided?"
          keyName="fireProtection.isWetControllerSystemProvided"
          formik={formik}
          isRequired
          onChange={(e) => {
            setFieldValue("fireProtection.numberOfWetControllerSystem", "");
            setFieldValue(
              "fireProtection.isWetControllerSystemProvided",
              !!+e.target.value
            );
          }}
        />

        {isWetControllerSystemProvided && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Number of Risers") + "*"}
              name="fireProtection.numberOfWetControllerSystem"
              formik={formik}
              placeholder="Enter Number of Risers"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={2}
        sx={{ pl: 1, minHeight: "70px" }}
      >
        <YesNoRadioButton
          label="Have internal hydrants been provided?"
          keyName="fireProtection.isInternalHydrantProvided"
          formik={formik}
          isRequired
          onChange={(e) => {
            setFieldValue("fireProtection.numberOfInternalHydrant", "");
            setFieldValue(
              "fireProtection.isInternalHydrantProvided",
              !!+e.target.value
            );
          }}
        />

        {isInternalHydrantProvided && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Number of Hydrants") + "*"}
              name="fireProtection.numberOfInternalHydrant"
              formik={formik}
              placeholder="Enter Number of Hydrants"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={2}
        sx={{ pl: 1, minHeight: "70px" }}
      >
        <YesNoRadioButton
          label="Have yard hydrants been provided?"
          keyName="fireProtection.isYardHydrantProvided"
          formik={formik}
          isRequired
          onChange={(e) => {
            setFieldValue("fireProtection.numberOfYardHydrant", "");
            setFieldValue(
              "fireProtection.isYardHydrantProvided",
              !!+e.target.value
            );
          }}
        />

        {isYardHydrantProvided && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Number of Hydrants") + "*"}
              name="fireProtection.numberOfYardHydrant"
              formik={formik}
              placeholder="Enter Number of Hydrants"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* Add & Remove of Basements and Floors */}
      <AutomaticSprinklerSystem formik={formik} floorOptions={floorOptions} />

      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t(
              "Special fire protection system provided, if any (water spray, foam, water mist, clean agent, etc)"
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Special Fire Protection System")}
            name="fireProtection.specialFireProtectionSystemProvided"
            formik={formik}
            placeholder="Enter Details"
          />
        </Grid>
      </Grid>
      <FireFightingPumpDetails formik={formik} />
      <FloorWiseFireExtinguisherDetails formik={formik} />
      <Grid item xs={12}>
        <Typography className="mb-3 fw-bold" lineHeight={1} variant="h6">
          {t("Fire Alarm & Detector System")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography>
          {t(
            "Is the building provided with Automatic Fire Detection and Alarm system (AFDAS)/ Manually Operated Fire Alarm system (MOFAS)?"
          )}
        </Typography>
      </Grid>

      {/* Add & Remove of Fire Detection System */}
      <FireDetectionSystem formik={formik} floorOptions={floorOptions} />

      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t("Location of Main Fire detection & alarm system of building")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Location") + "*"}
            name="fireProtection.location"
            formik={formik}
            placeholder="Enter Location"
          />
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is Main Fire detection & alarm system of building is configured in IBMS."
          keyName="fireProtection.isConfiguredInIBMS"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is Fire detection & alarm system installed in all tenant’s area."
          keyName="fireProtection.isInstalledInTenantArea"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is Fire detection & alarm system of all tenants is integrated with access control system of tenant area."
          keyName="fireProtection.isIntegratedWithTenantAccessControl"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is Fire detection & alarm system of all tenants is integrated with main Fire detection & alarm system of building."
          keyName="fireProtection.isIntegratedWithMainSystem"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is Main Fire detection & alarm system of building integrated with access control system, Lifts, HVAC system, PA system, Fire pumps, Flow switches etc of building."
          keyName="fireProtection.isIntegratedWithBuildingSystem"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is Main Fire detection & alarm system panel of building is integrated with GIFT City command & control center."
          keyName="fireProtection.isIntegratedWithCommandCenter"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t("Cause & effect matrix of fire detection & alarm system")}
            {<span className="text-danger">*</span>}
          </Typography>
        </Grid>
        <Grid item xs={12} className="mb-3 ms-1 mt-1">
          <div className="d-flex gap-3 flex-wrap">
            {(!!causeAndEffectMatrixDocumentUrl ||
              !!causeAndEffectMatrixDocument) && (
              <div className="image-container">
                <a
                  href={
                    causeAndEffectMatrixDocumentUrl
                      ? getImageUrl(causeAndEffectMatrixDocumentUrl)
                      : URL.createObjectURL(causeAndEffectMatrixDocument)
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <Box
                    component="img"
                    className="image d-block m-auto"
                    alt="document image"
                    src={
                      getFileExtension(
                        (causeAndEffectMatrixDocumentUrl
                          ? causeAndEffectMatrixDocumentUrl
                          : causeAndEffectMatrixDocument.name
                        ).toLowerCase()
                      ) === "pdf"
                        ? pdfImg
                        : causeAndEffectMatrixDocumentUrl
                        ? getImageUrl(causeAndEffectMatrixDocumentUrl)
                        : URL.createObjectURL(causeAndEffectMatrixDocument)
                    }
                  />
                </a>
                <i
                  className="ri-close-circle-line fs-3 close-btn"
                  onClick={() => {
                    setFieldValue(
                      causeAndEffectMatrixDocumentUrl
                        ? "fireProtection.causeAndEffectMatrixDocumentUrl"
                        : "fireProtection.causeAndEffectMatrixDocument",
                      ""
                    );
                  }}
                />
              </div>
            )}

            {!(causeAndEffectMatrixDocumentUrl
              ? causeAndEffectMatrixDocumentUrl
              : causeAndEffectMatrixDocument) && (
              <>
                <Box
                  component="label"
                  className="m-0 p-0 cursor-pointer"
                  sx={{ border: "1px dashed black" }}
                >
                  <UploadImage>
                    <AddIcon
                      sx={{
                        color: "black",
                      }}
                    />
                  </UploadImage>
                  <input
                    ref={ref}
                    type="file"
                    accept="image pdf/*"
                    onChange={(e) => fileChangeHandler(e.target.files[0])}
                    hidden
                  />
                </Box>

                {isError && (
                  <Box sx={{}}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.error.main, pt: 1.2 }}
                    >
                      {t(errorMessage)}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

FireProtection.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
  floorOptions: PropTypes.array,
};

export default withTranslation()(FireProtection);
